// Pretty-print Jinja + HTML template bodies for the code editor.
//
// Conservative and render-safe: it only inserts line breaks at *block-level* boundaries
// (block HTML tags and block-level Jinja statements) and never inside an inline run, so
// the rendered document is unchanged — whitespace adjacent to block boundaries is collapsed
// by HTML rendering anyway. Inline elements (<strong>, <a>, …), `{{ expr }}` and inline
// `{% if %}` stay on their line. Unknown tags are treated as inline (we never break what we
// don't recognise). Idempotent: formatting already-formatted input is stable.

const BLOCK = new Set([
  "address", "article", "aside", "blockquote", "body", "caption", "col", "colgroup",
  "dd", "div", "dl", "dt", "fieldset", "figcaption", "figure", "footer", "form",
  "h1", "h2", "h3", "h4", "h5", "h6", "head", "header", "hgroup", "hr", "html", "li",
  "main", "nav", "ol", "p", "pre", "section", "table", "tbody", "td", "tfoot", "th",
  "thead", "title", "tr", "ul",
]);
const VOID = new Set([
  "area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "param",
  "source", "track", "wbr",
]);
const JINJA_OPEN = new Set([
  "if", "for", "block", "with", "macro", "call", "filter", "autoescape", "trans", "spaceless",
]);
const JINJA_MID = new Set(["else", "elif", "empty"]);

interface Frame {
  block: boolean;
}

export function prettyHtml(src: string): string {
  // Split into structural tokens (tags, Jinja, comments) and the text between them.
  const tokenRe = /<!--[\s\S]*?-->|<[^>]*>|{{[\s\S]*?}}|{%[\s\S]*?%}|{#[\s\S]*?#}/g;
  const parts: string[] = [];
  let last = 0;
  for (let m = tokenRe.exec(src); m; m = tokenRe.exec(src)) {
    if (m.index > last) parts.push(src.slice(last, m.index));
    parts.push(m[0]);
    last = m.index + m[0].length;
  }
  if (last < src.length) parts.push(src.slice(last));

  const out: string[] = [];
  let cur = "";
  let depth = 0;
  const stack: Frame[] = [];
  const pad = () => "  ".repeat(Math.max(0, depth));
  const atBlockPos = () => cur.replace(/^\s+|\s+$/g, "") === "";
  const flush = () => {
    const line = cur.replace(/^\s+|\s+$/g, ""); // trim line ends only — internal spacing kept
    if (line) out.push(pad() + line);
    cur = "";
  };
  const line = (tok: string) => out.push(pad() + tok.replace(/\s+/g, " ").trim());

  for (const tok of parts) {
    if (tok.startsWith("<!--")) {
      flush();
      line(tok);
    } else if (tok.startsWith("<")) {
      const closing = tok.startsWith("</");
      const name = (tok.match(/^<\/?\s*([a-zA-Z0-9:-]+)/)?.[1] ?? "").toLowerCase();
      if (!BLOCK.has(name)) {
        cur += tok; // inline element — keep it on the line
      } else if (closing) {
        flush();
        depth -= 1;
        line(tok);
      } else if (tok.endsWith("/>") || VOID.has(name)) {
        flush();
        line(tok);
      } else {
        flush();
        line(tok);
        depth += 1;
      }
    } else if (tok.startsWith("{%")) {
      const kw = (tok.match(/^{%-?\s*([a-zA-Z]+)/)?.[1] ?? "").toLowerCase();
      if (kw.startsWith("end")) {
        const fr = stack.pop();
        if (fr?.block) {
          flush();
          depth -= 1;
          line(tok);
        } else {
          cur += tok;
        }
      } else if (JINJA_OPEN.has(kw)) {
        if (atBlockPos()) {
          flush();
          line(tok);
          depth += 1;
          stack.push({ block: true });
        } else {
          cur += tok;
          stack.push({ block: false });
        }
      } else if (JINJA_MID.has(kw)) {
        const fr = stack[stack.length - 1];
        if (fr?.block && atBlockPos()) {
          depth -= 1;
          line(tok);
          depth += 1;
        } else {
          cur += tok;
        }
      } else if (atBlockPos()) {
        flush(); // set / include / extends … at a block position → own line
        line(tok);
      } else {
        cur += tok;
      }
    } else if (tok.startsWith("{{") || tok.startsWith("{#")) {
      cur += tok; // inline expression / comment
    } else {
      cur += tok.replace(/\s+/g, " "); // text — collapse internal whitespace
    }
  }
  flush();
  return out.join("\n");
}
